import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Grid
} from "@mui/material";
import { LineChart, BarChart } from "@mui/x-charts";
import { callBackendApi } from "../../../methods/axiosInstances/backendInstance/apiRequest";

const FinancialMetricsCard = ({ title, value }) => (
  <Card sx={{ p: 2 }}>
    <CardContent>
      <Typography variant="subtitle2" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h5">{value}</Typography>
    </CardContent>
  </Card>
);

const StripeFinancialDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [mrrTrend, setMrrTrend] = useState([]);
  const [churnTrend, setChurnTrend] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [disputeTrend, setDisputeTrend] = useState([]);
  const [geoData, setGeoData] = useState([]);
  const [ltvBuckets, setLtvBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      const response = await callBackendApi("stripe/financial-metrics");
      if (response.success) {
        setMetrics(response.data.summary);
        setChartData(response.data.monthlyRevenueTrend || []);
        setMrrTrend(response.data.mrrTrend || []);
        setChurnTrend(response.data.churnTrend || []);
        setDisputeTrend(response.data.disputeTrend || []);
        setGeoData(response.data.geoDistribution || []);
        setLtvBuckets(response.data.ltvBuckets || []);
        setHasAccess(true);
      } else {
        setHasAccess(false);
      }
      setLoading(false);
    };

    fetchMetrics();
  }, []);

  if (loading) return <CircularProgress sx={{ mt: 4 }} />;
  if (!hasAccess) {
    return (
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          Stripe API access not available. Please configure your API key.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Financial Dashboard
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <FinancialMetricsCard title="Monthly Revenue" value={`€${metrics.monthlyRevenue}`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FinancialMetricsCard title="MRR" value={`€${metrics.mrr}`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FinancialMetricsCard title="ARR" value={`€${metrics.arr}`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FinancialMetricsCard title="Churn Rate" value={`${metrics.churnRate}%`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FinancialMetricsCard title="Dispute Rate" value={`${metrics.disputeRate}%`} />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Revenue Trend (Last 6 Months)
          </Typography>
          <LineChart
            dataset={chartData}
            xAxis={[{ scaleType: "band", dataKey: "month" }]}
            series={[{ dataKey: "revenue", label: "Revenue (€)", color: "#8884d8" }]}
            height={300}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            MRR Trend (Last 6 Months)
          </Typography>
          <LineChart
            dataset={mrrTrend}
            xAxis={[{ scaleType: "band", dataKey: "month" }]}
            series={[{ dataKey: "mrr", label: "MRR (€)", color: "#82ca9d" }]}
            height={300}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Churn Rate Trend (Last 6 Months)
          </Typography>
          <LineChart
            dataset={churnTrend}
            xAxis={[{ scaleType: "band", dataKey: "month" }]}
            yAxis={[{ min: 0, max: 100 }]}
            series={[{ dataKey: "churnRate", label: "Churn Rate (%)", color: "#ff7300" }]}
            height={300}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Dispute Rate Trend (Last 6 Months)
          </Typography>
          <LineChart
            dataset={disputeTrend}
            xAxis={[{ scaleType: "band", dataKey: "month" }]}
            yAxis={[{ min: 0, max: 100 }]}
            series={[{ dataKey: "disputeRate", label: "Dispute Rate (%)", color: "#d32f2f" }]}
            height={300}
          />
        </Grid>

        {geoData.length > 0 && (
          <Grid item xs={12} md={12}>
            <Typography variant="h6" gutterBottom>
              Customer Geo Distribution
            </Typography>
            <BarChart
              dataset={geoData}
              xAxis={[{ scaleType: "band", dataKey: "country", label: "Country" }]}
              series={[{ dataKey: "customers", label: "Customers" }]}
              height={300}
            />
          </Grid>
        )}

        {ltvBuckets.length > 0 && (
          <Grid item xs={12} md={12}>
            <Typography variant="h6" gutterBottom>
              Customer LTV Distribution
            </Typography>
            <BarChart
              dataset={ltvBuckets}
              xAxis={[{ scaleType: "band", dataKey: "bucket", label: "LTV Range (€)" }]}
              series={[{ dataKey: "count", label: "Customers" }]}
              height={300}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default StripeFinancialDashboard;
